import { formatAmountWithUnit } from "@loot-os/api-contract";
import { Field, FieldDescription, FieldError, FieldLabel } from "@loot-os/ui/components/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@loot-os/ui/components/input-group";
import { useId, useState } from "react";

import { useCurrency } from "../lib/currency.js";
import { giftCodeErrorMessage, useRedeemGiftCode } from "../lib/giftCodes.js";

/** The balance card's gift code field: redeems a code and says what it added, or why not. */
export function RedeemGiftCodeForm({ disabled }: { disabled: boolean }) {
  const id = useId();
  const currency = useCurrency();
  const redeem = useRedeemGiftCode();
  const [code, setCode] = useState("");
  const [outcome, setOutcome] = useState<{ redeemed: boolean; message: string } | null>(null);
  const refused = outcome?.redeemed === false;

  return (
    <form
      className="border-t border-border pt-3"
      onSubmit={(e) => {
        e.preventDefault();
        const typed = code.trim();
        if (!typed) return;
        setOutcome(null);
        redeem.mutate(typed, {
          onSuccess: (redemption) => {
            setCode("");
            setOutcome({
              redeemed: true,
              message: `Code redeemed: ${formatAmountWithUnit(redemption.amountCents, currency)} added to your balance.`,
            });
          },
          onError: (err) => setOutcome({ redeemed: false, message: giftCodeErrorMessage(err) }),
        });
      }}
    >
      <Field data-invalid={refused}>
        <FieldLabel htmlFor={id}>Gift code</FieldLabel>
        <InputGroup>
          <InputGroupInput
            id={id}
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              if (refused) setOutcome(null);
            }}
            placeholder="XXXX-XXXX-XXXX-XXXX"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={64}
            aria-invalid={refused}
            disabled={disabled}
            className="font-mono uppercase placeholder:normal-case"
          />
          <InputGroupAddon align="inline-end">
            <InputGroupButton
              type="submit"
              variant="default"
              disabled={disabled || redeem.isPending || code.trim() === ""}
            >
              {redeem.isPending ? "Redeeming…" : "Redeem"}
            </InputGroupButton>
          </InputGroupAddon>
        </InputGroup>
        {refused && <FieldError>{outcome.message}</FieldError>}
        {outcome?.redeemed && (
          <FieldDescription className="text-emerald-600">{outcome.message}</FieldDescription>
        )}
      </Field>
    </form>
  );
}
